import { useState } from "react";
import { ButtonT1 } from "../../components/atoms/Button";
import { BlurModal } from "../../app/newLayout/page/components/BlurModal/BlurModal";
import { CreateViewCss } from "../../app/newLayout/page/components/CreateView/CreateView";
import { ReactComponent as Close } from "../../app/newLayout/assets/svgIcon/listView/Close.svg";
import { ReactComponent as DragMenu } from "../../app/newLayout/assets/svgIcon/listView/dragmenu.svg";

/**
 * Board-view style editor for dashboard card visibility and sequence.
 * @param {Object} props Modal properties.
 * @return {JSX.Element}
 */
export function DashboardLayoutModal({ cards, selectedKeys, onApply, onClose }) {
  const [selected, setSelected] = useState(() =>
    selectedKeys.map(key => cards.find(card => card.key === key)).filter(Boolean),
  );

  /** @param {Object} event Checkbox change from the available list. */
  const onToggleAvailable = event => {
    const { value, checked } = event.target;
    if (value === "selectAll") {
      setSelected(checked ? [...cards] : []);
      return;
    }
    const card = cards.find(item => item.key === value);
    if (!card) return;
    setSelected(current =>
      checked ? [...current, card] : current.filter(item => item.key !== value),
    );
  };

  /** @param {Object} event Checkbox change from the selected list. */
  const onToggleSelected = event => {
    if (!event.target.checked) {
      setSelected(current => current.filter(item => item.key !== event.target.value));
    }
  };

  /** @param {Object} event Drag start. @param {number} index Current index. */
  const onDragStart = (event, index) => {
    event.dataTransfer.setData("dragIndex", String(index));
  };

  /** @param {Object} event Drop target. @param {number} dropIndex Destination index. */
  const onDrop = (event, dropIndex) => {
    const dragIndex = Number(event.dataTransfer.getData("dragIndex"));
    if (Number.isNaN(dragIndex) || dragIndex === dropIndex) return;
    setSelected(current => {
      const next = [...current];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
  };

  return (
    <BlurModal>
      <CreateViewCss
        className="editColumns operations-layout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="operations-layout-title"
      >
        <header>
          <span id="operations-layout-title">Edit dashboard</span>
          <Close onClick={onClose} aria-label="Close" />
        </header>
        <main>
          <div className="detailsOfView">
            <div className="leftSide">
              <div className="availableColumn">
                <p>Available cards</p>
                <div>
                  <label className="selectAll">
                    <input
                      type="checkbox"
                      value="selectAll"
                      checked={cards.length > 0 && selected.length === cards.length}
                      onChange={onToggleAvailable}
                    />
                    SELECT ALL
                  </label>
                  {cards.map(card => (
                    <label key={card.key}>
                      <input
                        type="checkbox"
                        value={card.key}
                        checked={selected.some(item => item.key === card.key)}
                        onChange={onToggleAvailable}
                      />
                      {card.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="rightSide">
              <p>Selected cards</p>
              <div className="selectedColumns">
                {selected.map((card, index) => (
                  <label
                    key={card.key}
                    className="item"
                    draggable
                    onDragStart={event => onDragStart(event, index)}
                    onDragOver={event => event.preventDefault()}
                    onDrop={event => onDrop(event, index)}
                  >
                    <DragMenu aria-hidden="true" />
                    <input type="checkbox" checked value={card.key} onChange={onToggleSelected} />
                    {card.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </main>
        <footer>
          <ButtonT1 $variant="primaryLight" title="Cancel" onClickCallback={onClose} />
          <ButtonT1
            title="Apply"
            onClickCallback={() =>
              onApply({
                sequence: [
                  ...selected.map(card => card.key),
                  ...cards.map(card => card.key).filter(key => !selected.some(item => item.key === key)),
                ],
                hidden: cards.map(card => card.key).filter(key => !selected.some(item => item.key === key)),
              })
            }
          />
        </footer>
      </CreateViewCss>
    </BlurModal>
  );
}
